/** @jsx jsx */
import { useEffect } from 'react';
import { css, jsx } from '@emotion/react'
import { graphql } from 'gatsby';
import { useLocalization } from "gatsby-theme-i18n";
import PageHeadline from '../components/PageHeadline';

import { getImage, withArtDirection } from "gatsby-plugin-image"
import CrosssiteBannerSection from '../sections/common/CrosssiteBannerSection';
import CategorySection from '../sections/fan-art-contest/CategorySection';
import SelectionSection from '../sections/fan-art-contest/SelectionSection';

import SectionHeadline from '../components/SectionHeadline'

import AnimatedSection from '../components/AnimatedSection';
import RoundButton from '../components/RoundButton';
import { parseHTML } from '../functions/common'

import Seo from "../components/SEO"

import * as breakpoint from '../styles/breakpoints.module.scss';
import ListingSection from '../sections/fan-art-contest/ListingSection';


const catColor = {
  "ilw" : "#D32F2F",
  "phw" : "#F57C00",
  "ssu" : "#388E3C",
  "ssa" : "#0288D1"
}

const ArtContestPage = ({pageContext,data}) => {
  
  const { addString, t,localeKey, currentLocaleObj } = useLocalization();
  const { section } = pageContext;
  const { list, total } = data;

  useEffect(() => {
    addString('fan_art_contest',data.allActivitiesFanArtContestCsv.nodes)
  },[addString,data]);

  return(<div css={css`background:#F7F7FA;
  

    .sectionHeadline{
      @media only screen and (max-width: ${breakpoint.smMax}){
        padding:0 80px;
      }
      @media only screen and (max-width: ${breakpoint.xsMax}){
        padding:0 24px;
      }
    }
  `}>
    <Seo 
        title={data.pageTitle[localeKey]} 
        description={data.pageDescription[localeKey]}  />  
    <PageHeadline
      category={t('fan_art_contest.page.display.title')} 
      title={t('fan_art_contest.contest.entry.title')} 
      subtitle={t('fan_art_contest.page.display.description')}
      bgImg={
      withArtDirection(getImage(data.headlineBg), [
        {
          media: `(min-width: ${breakpoint.mdMin})`,
          image: getImage(data.headlineBg),
        },
        {
          media: `(max-width: ${breakpoint.smMax}) and (min-width: ${breakpoint.smMin})`,
          image: getImage(data.headlineBg_tablet),
        },
        {
          media: `(max-width: ${breakpoint.xsMax})`,
          image: getImage(data.headlineBg_mobile),
        }
      ])}/>
    
    <CategorySection type={'entry'} currentSection={section}/>
    <AnimatedSection>
      <SectionHeadline 
        icon={section}
        color={catColor[section]}
        title={t(`fan_art_contest.contest.categories.${section}.title`)} 
        subtitle={t(`fan_art_contest.contest.entry.court.label`).replace('$count',total.pageInfo.totalCount)}
      />
    </AnimatedSection>
    <ListingSection list={list} currentSection={section} path={`fan-art-contest/entry`} />
    <SelectionSection currentSection={section} currentPage={'entry'}/>
    <div css={css`text-align:center;`}>
      <RoundButton size={'large'} color={'#4E4E50'} to={`/${currentLocaleObj.prefix}/activity/fan-art-contest/`} css={css`
        height:auto;
        margin:0 auto;
        font-size: 28px;
        line-height: 32px;
        padding:16px 32px;
        @media only screen and (max-width: ${breakpoint.smMax}){
          padding:14px 30px;
          height:auto;
          font-size: 16px;
          line-height: 24px;
        }
        @media only screen and (max-width: ${breakpoint.xsMax}){
          padding: 6px 30px;
          font-size: 16px;
          line-height: 24px;
          height:auto;
        }
      `}>
        {parseHTML(t('fan_art_contest.contest.foot.nav.top'))}
      </RoundButton>
    </div>
    <CrosssiteBannerSection />
  </div>);
};

export default ArtContestPage;

export const pageQuery = graphql`
  query($pageLimit: Int!, $pageAnchor: Int!, $section: String!) {
    pageTitle : activitiesFanArtContestCsv(key: {regex: "/^page.title/"}) {
      ...localizedContent
    }
    pageDescription :activitiesFanArtContestCsv(key: {regex: "/^page.description/"}) {
      ...localizedContent
    }

    allActivitiesFanArtContestCsv {
      nodes {
        ...localizedContent
      }
    }

    headlineBg : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "content/activity/FanArtContest/bg_pageHeader.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }
    headlineBg_tablet : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "content/activity/FanArtContest/bg_pageHeader_tablet.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }
    headlineBg_mobile : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "content/activity/FanArtContest/bg_pageHeader_mobile.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }

    

    total :  allExternalFanArtContestEntryCsv(
      sort: {fields: fields___order, order: ASC}, 
      filter: {section: {eq: $section }, nickname: {ne: ""}, deleted: {ne: "true"}}
      limit: $pageLimit
      skip: $pageAnchor
    ) {
      pageInfo {
        totalCount
      }
    }

    list : allExternalFanArtContestEntryCsv(
      sort: {fields: fields___order, order: ASC}, 
      filter: {section: {eq: $section }, nickname: {ne: ""}}
      limit: $pageLimit
      skip: $pageAnchor
    ) {
      nodes {
        title
        nickname
        number
        deleted
        images {
          thumb {
            height
            url
            width
          }
          origin {
            height
            url
            width
          }
        }
      }
      pageInfo {
        currentPage
        hasNextPage
        hasPreviousPage
        totalCount
        pageCount
      }
    }

   
  }
`
